import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import type { PriceComponent } from '../../api/objects'
import {
  monthlyComponents,
  replacePrices,
  upfrontComponents,
} from '../../api/objects-write'
import type { PriceComponentInput } from '../../api/objects-write'
import { t } from '../../i18n'

/**
 * What an offer costs.
 *
 * The one thing an object exists for, and the reason this form is not a single
 * "price" box. A client is told a total, and a total assembled from a rent plus
 * a guess at the bills is a number nobody has: invariant 2 says an unstated
 * charge is unknown, so an empty field here stays empty all the way through and
 * the monthly cost comes back unknown rather than as the rent alone.
 *
 * Which is why leaving a row blank is a real answer and the form never fills one
 * in. A person who does not know the condominio charge leaves it, and the system
 * says so instead of quoting a figure that will be wrong in November.
 *
 * A room may also be priced per occupancy -- 500 for one, 700 for two -- and
 * those are separate rows for the same charge, because the third tenant is
 * cheaper than the second and a per-person figure would compute numbers nobody
 * quoted.
 */
/** One of the charges the system knows, so its label is a key that exists. */
type ChargeName = (typeof monthlyComponents)[number] | (typeof upfrontComponents)[number]

interface Row {
  readonly key: string
  readonly component: ChargeName
  amount: string
  estimated: boolean
  months: string
  occupants: string
}

function rowsFrom(components: readonly PriceComponent[]): Row[] {
  return components.map((component, index) => ({
    key: `${component.component}-${index}`,
    component: component.component as ChargeName,
    // An unknown amount has no figure, and the field it lands in is empty. That
    // emptiness is the record, not a missing value somebody forgot to type.
    // An unknown amount holds no figure at all -- that is the type being
    // honest, not a nullable that needs defending against. The field is empty,
    // and the emptiness is the record.
    amount:
      component.amount.amountMinor === null || component.amount.kind === 'unknown'
        ? ''
        : String(component.amount.amountMinor / 100),
    estimated: component.amount.kind === 'estimated',
    // Stored in hundredths so no comparison goes through a float; shown as the
    // number a person said.
    months:
      component.monthsHundredths === undefined ? '' : String(component.monthsHundredths / 100),
    occupants: component.forOccupants === undefined ? '' : String(component.forOccupants),
  }))
}

export function PriceForm({
  listingID,
  propertyID,
  components,
}: {
  readonly listingID: string
  readonly propertyID: string
  readonly components: readonly PriceComponent[]
}) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<Row[]>(() => rowsFrom(components))
  const [failure, setFailure] = useState<string | null>(null)

  const client = useQueryClient()

  const save = useMutation({
    // Every row goes, including the empty ones. A charge with no figure still
    // says something -- that it exists and nobody has measured it -- and that is
    // exactly what makes the monthly total unknown rather than silently
    // complete.
    mutationFn: () => replacePrices(listingID, rows.map(toInput)),
    onSuccess: () => {
      setFailure(null)
      setOpen(false)
      void client.invalidateQueries({ queryKey: ['property', propertyID] })
    },
    onError: (error: Error) => setFailure(error.message),
  })

  if (!open) {
    return (
      <Button
        variant="secondary"
        onClick={() => {
          // Re-read on opening rather than on mount: the card may have been
          // refreshed under the form since it was last closed.
          setRows(rowsFrom(components))
          setOpen(true)
        }}
      >
        {components.length === 0 ? t('price.setPrice') : t('price.editPrice')}
      </Button>
    )
  }

  const addRow = (component: ChargeName) =>
    setRows((current) => [
      ...current,
      {
        key: `${component}-${current.length}-${String(Date.now())}`,
        component,
        amount: '',
        estimated: false,
        months: '',
        occupants: '',
      },
    ])

  const update = (key: string, patch: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  return (
    <form
      className="build-form"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <p className="build-form__hint">{t('price.hint')}</p>

      <table className="price-form">
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="price-form__name">{t(`price.component.${row.component}`)}</td>
              <td>
                <input
                  className="field__input price-form__amount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={row.amount}
                  onChange={(event) => update(row.key, { amount: event.target.value })}
                  placeholder={t('price.unknown')}
                  aria-label={t(`price.component.${row.component}`)}
                />
              </td>
              <td>
                {/* Only rooms are priced per head, but the field is offered on
                    every recurring charge rather than guessed at: a flat let to
                    a couple at a different rate is somebody's real arrangement. */}
                <input
                  className="field__input price-form__occupants"
                  type="number"
                  min={1}
                  value={row.occupants}
                  onChange={(event) => update(row.key, { occupants: event.target.value })}
                  placeholder={t('price.anyOccupancy')}
                  aria-label={t('price.forOccupants')}
                />
              </td>
              <td>
                {row.component === 'deposit' ? (
                  <input
                    className="field__input price-form__months"
                    type="number"
                    min={0}
                    step="0.5"
                    value={row.months}
                    onChange={(event) => update(row.key, { months: event.target.value })}
                    placeholder={t('price.months')}
                    aria-label={t('price.months')}
                  />
                ) : null}
              </td>
              <td>
                <label className="price-form__estimated">
                  <input
                    type="checkbox"
                    checked={row.estimated}
                    onChange={(event) => update(row.key, { estimated: event.target.checked })}
                  />
                  {t('price.estimated')}
                </label>
              </td>
              <td>
                <button
                  type="button"
                  className="price-form__drop"
                  onClick={() => setRows((current) => current.filter((one) => one.key !== row.key))}
                  aria-label={t('price.drop')}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="price-form__add">
        {[...monthlyComponents, ...upfrontComponents].map((component) => (
          <button
            key={component}
            type="button"
            className="status-picker__option"
            onClick={() => addRow(component)}
          >
            + {t(`price.component.${component}`)}
          </button>
        ))}
      </div>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button type="submit" busy={save.isPending}>
          {save.isPending ? t('build.creating') : t('price.save')}
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)} disabled={save.isPending}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

/**
 * An empty amount is sent as an absent one, which is what makes it unknown
 * rather than zero on the other side.
 */
function toInput(row: Row): PriceComponentInput {
  const amount = row.amount.trim()
  const months = row.months.trim()
  const occupants = row.occupants.trim()

  return {
    component: row.component,
    ...(amount === '' ? {} : { amount: Number(amount), estimated: row.estimated }),
    ...(months === '' ? {} : { months: Number(months) }),
    ...(occupants === '' ? {} : { for_occupants: Number(occupants) }),
  }
}
